import React, { useEffect, useRef, useState } from 'react';
import Chart from 'chart.js/auto';
import { fetchHistogram, fetchArticles, fetchCountryData } from './App';
import { fetchDemographics } from './DemographicsPage';
import {
  FaChartLine,
  FaTv,
  FaChartBar,
  FaNetworkWired,
  FaMapMarkedAlt,
  FaSlidersH,
  FaUserFriends,
  FaCloud,
  FaFilter,
  FaSmile,
  FaMeh,
  FaFrown,
  FaExclamation,
  FaCalendarAlt,
  FaThLarge,
  FaBell,
  FaUserCircle,
  FaEllipsisV,
  FaDownload,
  FaSearch,
  FaSync,
  FaBookmark,
  FaChevronDown
} from 'react-icons/fa';
import WorldMapPage from './WorldMapPage';
import DemographicsPage from './DemographicsPage';
import TopThemesPage from './TopThemesPage';
import InfluencerPage from './InfluencerPage';
import InfluencerDetailPage from './InfluencerDetailPage';
import LanguagesFilter from './LanguagesFilter';
import DemographicsDropdown from './DemographicsDropdown';
import BookmarksDropdown from './BookmarksDropdown';
import ProductsDropdown from './ProductsDropdown';
import NotificationsDropdown from './NotificationsDropdown';
import UserDropdown from './UserDropdown';
import UserStatusDropdown from './UserStatusDropdown';
import FiltersDropdown from './FiltersDropdown';
import CountriesDropdown from './CountriesDropdown';
import SavedSearchesDropdown from './SavedSearchesDropdown';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import MediaTypesDropdown from './MediaTypesDropdown';

export default function DashboardApp() {
  const [query, setQuery] = useState('UAE');
  const [resultTotal, setResultTotal] = useState('...');
  const [articles, setArticles] = useState([]);
  const [countryData, setCountryData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [metrics, setMetrics] = useState({
    engagement: '0',
    sentiment: { positive: '0', negative: '0', neutral: '0' },
    potentialReach: '0'
  });
  const [selectedDate, setSelectedDate] = useState(null);
  const [allArticles, setAllArticles] = useState([]);
  const [selectedArticles, setSelectedArticles] = useState([]);
  const [highlightedArticles, setHighlightedArticles] = useState([]);
  const [highlightedCountryData, setHighlightedCountryData] = useState([]);
  const [activePage, setActivePage] = useState('dashboard');
  const [selectedInfluencer, setSelectedInfluencer] = useState(null);
  const [selectedLanguageFilter, setSelectedLanguageFilter] = useState('');
  const [availableLanguages, setAvailableLanguages] = useState([]);
  const [showDemographicsFilter, setShowDemographicsFilter] = useState(false);
  const [demographicsFilters, setDemographicsFilters] = useState({
    noFilter: true,
    age: [],
    automatedAccounts: [],
    familyStatus: [],
    gender: [],
    individualBusiness: [],
    nonBinaryGender: [],
    occupations: [],
  });
  const [allDemographicsData, setAllDemographicsData] = useState(null);
  const [showBookmarksDropdown, setShowBookmarksDropdown] = useState(false);
  const [showProductsDropdown, setShowProductsDropdown] = useState(false);
  const [showNotificationsDropdown, setShowNotificationsDropdown] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showUserStatusDropdown, setShowUserStatusDropdown] = useState(false);
  const [showFiltersDropdown, setShowFiltersDropdown] = useState(false);
  const [showCountriesDropdown, setShowCountriesDropdown] = useState(false);
  const [selectedCountryFilter, setSelectedCountryFilter] = useState('');
  const [selectedDateRange, setSelectedDateRange] = useState('1D');
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);
  const [availableMinDate, setAvailableMinDate] = useState(new Date('2024-05-03'));
  const [availableMaxDate, setAvailableMaxDate] = useState(new Date('2025-06-03'));
  const [showSavedSearchesDropdown, setShowSavedSearchesDropdown] = useState(false);
  const [recentSearches, setRecentSearches] = useState([]);
  const [highlightedSentiment, setHighlightedSentiment] = useState(null);
  const [showMediaTypesDropdown, setShowMediaTypesDropdown] = useState(false);
  const [selectedMediaTypes, setSelectedMediaTypes] = useState(['All Media Type']);
  const [allMediaTypes, setAllMediaTypes] = useState([]);
  const [allFilters, setAllFilters] = useState({
    sentiment: [],
    mediaType: [],
    countries: [],
    demographics: [],
    textQuery: '',
  });
  const [currentStartDate, setCurrentStartDate] = useState(null);
  const [currentEndDate, setCurrentEndDate] = useState(null);
  const [currentQueryForMap, setCurrentQueryForMap] = useState('');

  const resultChartRef = useRef(null);
  const resultChartInstance = useRef(null);

  const sentimentChartRef = useRef(null);
  const sentimentChartInstance = useRef(null);

  // Effect to scroll to highlighted articles when they change
  useEffect(() => {
    if (highlightedArticles.length > 0) {
      const firstArticle = document.querySelector('.article-item.highlighted');
      if (firstArticle) {
        firstArticle.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }, [highlightedArticles]); // Rerun when highlightedArticles changes

  // Helper function to filter demographics data based on selected filters
  const getFilteredDemographicsData = () => {
    // If no data or no filter is active, return the raw data or a default structure
    if (!allDemographicsData || demographicsFilters.noFilter) {
      // Return a default structure if allDemographicsData is null or undefined
      return allDemographicsData || { 
        female: 0,
        male: 0,
        age: [],
        automatedAccounts: [],
        familyStatus: [],
        gender: [], // Although not used directly as an array here, good to include
        individualBusiness: [],
        nonBinaryGender: [], // Corrected from nonBinary in old fallback
        occupations: [],
      }; 
    }

    const filteredData = { ...allDemographicsData }; // Start with a copy of the original data

    // Filter Gender (if applicable)
    if (demographicsFilters.gender && demographicsFilters.gender.length > 0) {
      filteredData.female = demographicsFilters.gender.includes('female') ? allDemographicsData.female : 0;
      filteredData.male = demographicsFilters.gender.includes('male') ? allDemographicsData.male : 0;
    } else if (demographicsFilters.gender && demographicsFilters.gender.length === 0 && !demographicsFilters.noFilter) {
       // If gender filter is active but no options selected, show 0 for both
       filteredData.female = 0;
       filteredData.male = 0;
    }

    // Filter other array-based categories
    const categoriesToFilter = ['age', 'automatedAccounts', 'familyStatus', 'individualBusiness', 'nonBinaryGender', 'occupations'];
    categoriesToFilter.forEach(category => {
      if (demographicsFilters[category] && demographicsFilters[category].length > 0) {
        // Filter the array: keep items whose label is in the selected filters
        filteredData[category] = (allDemographicsData[category] || []).filter(item =>
          demographicsFilters[category].includes(item.label)
        );
      } else if (demographicsFilters[category] && demographicsFilters[category].length === 0 && !demographicsFilters.noFilter) {
         // If filter is active but no options selected, show empty array
         filteredData[category] = [];
      }
      // If filter for this category is not in demographicsFilters (i.e., checkbox not checked), we don't filter this category.
      // If noFilter is true, we return all data at the beginning of the function.
    });

    return filteredData;
  };

  const fetchAndRender = async (q) => {
    setLoading(true);
    setError(null);
    try {
      // Construct the query with languages if selected
      const finalQuery = selectedLanguageFilter ? 
        `${q} languages:${selectedLanguageFilter}` : 
        q;
      
      // Add country filter to the query if selected
      const finalQueryWithCountry = selectedCountryFilter ?
        `${finalQuery} country:${selectedCountryFilter}` :
        finalQuery;
      
      // Calculate start date based on selectedDateRange or use custom dates
      let startDate;
      let endDate = new Date(); // Default end date to today

      if (selectedDateRange === 'Custom' && customStartDate && customEndDate) {
        startDate = customStartDate;
        endDate = customEndDate;
      } else {
        startDate = new Date();
        switch (selectedDateRange) {
          case '1D':
            startDate.setDate(startDate.getDate() - 1);
            break;
          case '7D':
            startDate.setDate(startDate.getDate() - 7);
            break;
          case '30D':
            startDate.setDate(startDate.getDate() - 30);
            break;
          case '3M':
            startDate.setMonth(startDate.getMonth() - 3);
            break;
          case '6M':
            startDate.setMonth(startDate.getMonth() - 6);
            break;
          case '13M':
            startDate.setMonth(startDate.getMonth() - 13);
            break;
          default:
            startDate.setDate(startDate.getDate() - 1);
        }
      }
      // Use startDate and endDate in your API params
      const params = {
        StartDate: startDate.toISOString().split('T')[0],
        EndDate: endDate.toISOString().split('T')[0],
        Query: finalQueryWithCountry,
      };
      console.log('API Request Params:', params);
      // Fetch histogram data
      const histogram = await fetchHistogram(finalQueryWithCountry, params);
      console.log('Fetched Histogram Data:', histogram);
      const labels = histogram.map((entry) => entry.Date);
      const counts = histogram.map((entry) => entry.Count);
      const totalVolume = counts.reduce((a, b) => a + b, 0);
      setResultTotal(totalVolume.toLocaleString());

      // Fetch all articles and country data
      const [articles, countryData] = await Promise.all([
        fetchArticles(finalQueryWithCountry, params),
        fetchCountryData(finalQueryWithCountry, params)
      ]);
      
      setAllArticles(articles);
      setCountryData(countryData);
      setHighlightedArticles([]);
      setHighlightedCountryData([]);
      setSelectedDate(null);

      // Update state for WorldMapPage
      setCurrentStartDate(startDate);
      setCurrentEndDate(endDate);
      setCurrentQueryForMap(finalQueryWithCountry);

      // Calculate metrics from the latest data point
      const latestData = histogram[histogram.length - 1];
      
      // Calculate engagement (total volume)
      const engagement = latestData?.Count ? latestData.Count.toLocaleString() : '0';

      // Calculate sentiment percentages
      const totalLatest = latestData?.Count || 0;
      const sentiment = {
        positive: totalLatest ? ((latestData.volume_pos / totalLatest) * 100).toFixed(1) : '0',
        negative: totalLatest ? ((latestData.volume_neg / totalLatest) * 100).toFixed(1) : '0',
        neutral: totalLatest ? ((latestData.volume_neu / totalLatest) * 100).toFixed(1) : '0'
      };

      // Calculate potential reach (using trusted score)
      const potentialReach = latestData?.Count && latestData?.trusted ? 
        Math.round(latestData.Count * (1 + latestData.trusted)).toLocaleString() : '0';

      if (resultChartInstance.current) resultChartInstance.current.destroy();

      resultChartInstance.current = new Chart(resultChartRef.current, {
        type: 'line',
        data: {
          labels,
          datasets: [{
            label: 'Result Over Time',
            data: counts,
            fill: true,
            backgroundColor: 'rgba(102,153,255,0.2)',
            borderColor: 'rgba(102,153,255,1)',
            tension: 0.4,
            pointRadius: 6,
            pointHoverRadius: 8,
            pointStyle: 'circle',
            pointBackgroundColor: 'rgba(102,153,255,1)',
            pointBorderColor: '#fff',
            pointBorderWidth: 2,
          }],
        },
        options: {
          responsive: true,
          plugins: { 
            legend: { display: false },
            tooltip: {
              callbacks: {
                label: function(context) {
                  return `Volume: ${context.raw}`;
                }
              }
            }
          },
          scales: { y: { } },
          onClick: async (event, elements) => {
            if (elements && elements.length > 0) {
              const index = elements[0].index;
              const selectedDate = labels[index];
              setSelectedDate(selectedDate);
              
              // Find articles and country data that match the selected date
              const dateArticles = allArticles.filter(article => 
                article.Date && article.Date.startsWith(selectedDate.split(' ')[0])
              );
              
              const dateCountryData = countryData.filter(data => 
                data.Date && data.Date.startsWith(selectedDate.split(' ')[0])
              );
              
              // Update highlighted data
              setHighlightedArticles(dateArticles);
              setHighlightedCountryData(dateCountryData);

              console.log('Selected Date from Chart:', selectedDate);
              console.log('Filtered Articles Count:', dateArticles.length);
              console.log('Highlighted Articles State:', highlightedArticles); // This will show the state *before* it's updated in the next render cycle
            }
          }
        },
      });

      // Extract sentiment data
      const positiveVolumes = histogram.map(entry => entry.volume_pos || 0);
      const negativeVolumes = histogram.map(entry => entry.volume_neg || 0);
      const neutralVolumes = histogram.map(entry => entry.volume_neu || 0);

      // Destroy existing sentiment chart if it exists
      if (sentimentChartInstance.current) sentimentChartInstance.current.destroy();

      // Create the sentiment chart
      sentimentChartInstance.current = new Chart(sentimentChartRef.current, {
        type: 'line', // Use 'line' type for sentiment trends
        data: {
          labels,
          datasets: [
            {
              label: 'Positive',
              data: positiveVolumes,
              borderColor: highlightedSentiment === 'positive' ? '#58D68D' : '#2ecc71', // Brighter green for glow
              backgroundColor: highlightedSentiment === 'positive' ? 'rgba(88, 214, 141, 0.4)' : 'rgba(46, 204, 113, 0.2)', // More opaque green
              fill: true,
              tension: 0.4,
              borderWidth: highlightedSentiment === 'positive' ? 4 : 2, // Thicker if highlighted
            },
            {
              label: 'Negative',
              data: negativeVolumes,
              borderColor: highlightedSentiment === 'negative' ? '#F1948A' : '#e74c3c', // Brighter red for glow
              backgroundColor: highlightedSentiment === 'negative' ? 'rgba(241, 148, 138, 0.4)' : 'rgba(231, 76, 60, 0.2)', // More opaque red
              fill: true,
              tension: 0.4,
              borderWidth: highlightedSentiment === 'negative' ? 4 : 2, // Thicker if highlighted
            },
            {
              label: 'Neutral',
              data: neutralVolumes,
              borderColor: highlightedSentiment === 'neutral' ? '#F7DC6F' : '#fbc02d', // Brighter yellow for glow
              backgroundColor: highlightedSentiment === 'neutral' ? 'rgba(247, 220, 111, 0.4)' : 'rgba(247, 220, 111, 0.2)', // More opaque yellow
              fill: true,
              tension: 0.4,
              borderWidth: highlightedSentiment === 'neutral' ? 4 : 2, // Thicker if highlighted
            },
          ],
        },
        options: {
          responsive: true,
          plugins: {
            legend: {
              display: true, // Show legend for sentiment categories
              position: 'bottom', // Position legend at the bottom
            },
            tooltip: {
              mode: 'index', // Show tooltips for all datasets at the same date
              intersect: false,
            },
          },
          scales: {
            x: {
              title: {
                display: true,
                text: 'Date',
              },
            },
            y: {
              title: {
                display: true,
                text: 'Volume',
              },
            },
          },
        },
      });

      // Update the metrics state
      setMetrics({
        engagement,
        sentiment,
        potentialReach
      });

      // Save current search to recent searches
      const currentSearch = {
        query: q,
        startDate: params.StartDate,
        endDate: params.EndDate,
        dateRange: selectedDateRange,
      };

      setRecentSearches((prevSearches) => {
        // Avoid duplicates and keep a reasonable number of recent searches
        const updatedSearches = [currentSearch, ...prevSearches.filter(s => 
          s.query !== currentSearch.query || s.startDate !== currentSearch.startDate || s.endDate !== currentSearch.endDate
        )];
        return updatedSearches.slice(0, 5); // Keep last 5 unique searches
      });

      // Fetch demographics data separately
      try {
        const demoData = await fetchDemographics(finalQueryWithCountry); // Fetch demographics based on the main query and country filter
        setAllDemographicsData(demoData);
      } catch (demoErr) {
         console.warn('Could not fetch detailed demographics data:', demoErr);
         setAllDemographicsData(null); // Reset or use fallback if fetching fails
      }

    } catch (err) {
      console.error('Error in fetchAndRender:', err);
      setError(err.message || 'Failed to fetch data. Please try again later.');
      setAllArticles([]);
      setCountryData([]);
      setHighlightedArticles([]);
      setHighlightedCountryData([]);
      setSelectedDate(null);
      setResultTotal('0');
      setMetrics({
        engagement: '0',
        sentiment: { positive: '0', negative: '0', neutral: '0' },
        potentialReach: '0'
      });
      if (resultChartInstance.current) {
        resultChartInstance.current.destroy();
        resultChartInstance.current = null;
      }
    } finally {
      setLoading(false);
    }
  };

  // Fetch data when the component mounts or selected language filter changes
  useEffect(() => {
    // Fetch on mount, language filter, country filter, or date range change
    fetchAndRender(query);

    // Populate allMediaTypes with dummy data for now (replace with API call if available)
    setAllMediaTypes(['AcFun', 'Blogs', 'External', 'Facebook', 'Youtube', 'Twitter']);

  }, [query, selectedLanguageFilter, selectedCountryFilter, selectedDateRange, highlightedSentiment, selectedMediaTypes]);

  // Effect to fetch available languages when the component mounts (or as needed)
  useEffect(() => {
    // This would ideally fetch languages from an API
    // For now, using dummy data
    setAvailableLanguages([
      { label: 'English', value: 'en' },
      { label: 'Spanish', value: 'es' },
      { label: 'French', value: 'fr' },
      { label: 'German', value: 'de' },
      { label: 'Chinese', value: 'zh' },
    ]);
  }, []);

  const handleSearch = () => {
    if (query.trim() !== '') {
      fetchAndRender(query.trim());
    }
  };

  // Function to handle selecting a saved search
  const handleSelectSavedSearch = (search) => {
    setQuery(search.query);
    setSelectedDateRange(search.dateRange);
    if (search.dateRange === 'Custom') {
      setCustomStartDate(new Date(search.startDate));
      setCustomEndDate(new Date(search.endDate));
    } else {
      setCustomStartDate(null);
      setCustomEndDate(null);
    }
    // fetchAndRender will be called by the useEffect when query or date states change
  };

  // Function to handle influencer selection and navigate to detail page
  const handleSelectInfluencer = (influencer) => {
    setSelectedInfluencer(influencer);
    setActivePage('influencerDetail'); // Assuming a new page state for detail view
  };

  // Function to go back to the influencer list page
  const handleBackToInfluencerList = () => {
      setSelectedInfluencer(null);
      setActivePage('influencer');
  };

  // Function to handle languages filter change
  const handleLanguagesFilterChange = (selected) => {
    setSelectedLanguageFilter(selected);
    // Since it's a simple select, re-fetch data immediately when selection changes
    fetchAndRender(query); // Re-fetch using the current search query and new language filter
    console.log('Selected language:', selected);
  };

  // Function to toggle demographics filter dropdown visibility
  const handleToggleDemographicsFilter = () => {
    setShowDemographicsFilter(!showDemographicsFilter);
  };

  // Function to handle demographics filter change from the dropdown
  const handleDemographicsFilterChange = (newFilters) => {
    setDemographicsFilters(newFilters);
    // Data filtering will happen before passing to DemographicsPage
    console.log('Demographics filters updated:', newFilters);
  };

  // Function to handle country filter change
  const handleCountryFilterChange = (countryValue) => {
    setSelectedCountryFilter(countryValue);
    setShowCountriesDropdown(false); // Close dropdown after selection
  };

  // Function to toggle country filter dropdown visibility
  const handleToggleCountriesDropdown = () => {
    setShowCountriesDropdown(!showCountriesDropdown);
  };

  // Function to handle media type change
  const handleMediaTypeChange = (newSelection) => {
    setSelectedMediaTypes(newSelection);
    // Re-fetch data when media type changes
    fetchAndRender(query);
  };

  return (
    <div className="dashboard">
      {/* Header Bar */}
      <div className="header-bar">
        {/* Logo */}
        <div className="header-logo">UPDATED LOGO</div>

        {/* User Info */}
        <div className="user-info" onClick={() => setShowUserStatusDropdown(!showUserStatusDropdown)} style={{ cursor: 'pointer', position: 'relative' }}>
          <div className="user-name">Usama</div>
          <div className="user-status">Free Search</div>
          {showUserStatusDropdown && (
            <UserStatusDropdown onClose={() => setShowUserStatusDropdown(false)} />
          )}
        </div>

        {/* Spacer to push icons to the right */}
        <div style={{ flex: 1 }}></div>

        {/* Icons */}
        <div className="header-icons">
           <FaThLarge onClick={() => setShowProductsDropdown(!showProductsDropdown)} style={{ cursor: 'pointer' }}/> {/* Grid Icon with click handler */}
           <FaBookmark onClick={() => setShowBookmarksDropdown(!showBookmarksDropdown)} style={{ cursor: 'pointer' }}/> {/* Bookmark Icon with click handler */}
           <div style={{ position: 'relative' }}>
             <FaBell onClick={() => setShowNotificationsDropdown(!showNotificationsDropdown)} style={{ cursor: 'pointer' }}/> {/* Bell Icon with click handler */}
             {showNotificationsDropdown && (
               <NotificationsDropdown onClose={() => setShowNotificationsDropdown(false)} />
             )}
           </div>
           <div style={{ position: 'relative' }}>
             <FaUserCircle onClick={() => setShowUserDropdown(!showUserDropdown)} style={{ cursor: 'pointer' }}/> {/* User Circle Icon */}
             {showUserDropdown && (
               <UserDropdown onClose={() => setShowUserDropdown(false)} />
             )}
           </div>
           <FaChevronDown /> {/* Down arrow icon */}
           {/* Products Dropdown */}
           {showProductsDropdown && (
             <ProductsDropdown onClose={() => setShowProductsDropdown(false)} />
           )}
           {/* Bookmarks Dropdown */}
           {showBookmarksDropdown && (
             <BookmarksDropdown onClose={() => setShowBookmarksDropdown(false)} />
           )}
        </div>
      </div>

      {/* Content below the fixed header */}
      <div className="content-below-header">
        {/* Top Right actions below header */}
        <div className="top-right-actions">
          <button className="new-search-btn" onClick={() => setQuery('')}> <FaSearch /> New Search</button>
          <div style={{ position: 'relative' }}>
            <button className="saved-searches-btn" onClick={() => setShowSavedSearchesDropdown(!showSavedSearchesDropdown)}> <FaSearch /> Saved Searches</button>
            {showSavedSearchesDropdown && (
              <SavedSearchesDropdown 
                searches={recentSearches}
                onSelectSearch={handleSelectSavedSearch}
                onClose={() => setShowSavedSearchesDropdown(false)}
              />
            )}
          </div>
          <FaSync className="action-icon" /> {/* Refresh Icon */}
          <FaDownload className="action-icon" /> {/* Download Icon */}
        </div>
      {/* Sidebar */}
      <div className="sidebar">
          <div className={`sidebar-icon${activePage === 'dashboard' ? ' active' : ''}`} onClick={() => { setSelectedInfluencer(null); setActivePage('dashboard'); }}><FaChartLine /></div>
          <div className={`sidebar-icon${activePage === 'influencer' || activePage === 'influencerDetail' ? ' active' : ''}`} onClick={() => { setSelectedInfluencer(null); setActivePage('influencer'); }}><FaNetworkWired /></div>
          <div className={`sidebar-icon${activePage === 'worldmap' ? ' active' : ''}`} onClick={() => { setSelectedInfluencer(null); setActivePage('worldmap'); }}><FaMapMarkedAlt /></div>
          <div className={`sidebar-icon${activePage === 'demographics' ? ' active' : ''}`} onClick={() => { setSelectedInfluencer(null); setActivePage('demographics'); }}><FaUserFriends /></div>
          <div className={`sidebar-icon${activePage === 'topthemes' ? ' active' : ''}`} onClick={() => { setSelectedInfluencer(null); setActivePage('topthemes'); }}><FaCloud /></div>
      </div>

      {/* Main Content */}
      <div className="main">
        {/* Filter Bar */}
          <div className="filter-bar">
            <div style={{ position: 'relative' }}>
              <button className={`filter-btn ${showFiltersDropdown || Object.keys(demographicsFilters).some(key => key !== 'noFilter' && demographicsFilters[key]?.length > 0) ? 'active' : ''}`} onClick={() => setShowFiltersDropdown(!showFiltersDropdown)}>
            <FaFilter /> Filters
          </button>
              {showFiltersDropdown && (
                <FiltersDropdown
                  onClose={() => setShowFiltersDropdown(false)}
                  filters={allFilters}
                  onFilterChange={setAllFilters}
                />
              )}
            </div>
            <div className="sentiment-icons">
              <FaSmile className="fa-smile" onClick={() => {
                setHighlightedSentiment('positive');
                sentimentChartRef.current.scrollIntoView({ behavior: 'smooth' });
              }} />
              <FaMeh className="fa-meh" onClick={() => {
                setHighlightedSentiment('neutral');
                sentimentChartRef.current.scrollIntoView({ behavior: 'smooth' });
              }} />
              <FaFrown className="fa-frown" onClick={() => {
                setHighlightedSentiment('negative');
                sentimentChartRef.current.scrollIntoView({ behavior: 'smooth' });
              }} />
              <FaExclamation className="fa-exclamation" />
          </div>
          <div style={{ position: 'relative', display: 'inline-block' }}> {/* Wrap Media Types chip and dropdown in a positioned container */}
            <div className="chip active" onClick={() => setShowMediaTypesDropdown(!showMediaTypesDropdown)} style={{ cursor: 'pointer' }}>Media Types</div>
            {showMediaTypesDropdown && (
              <MediaTypesDropdown 
                onClose={() => setShowMediaTypesDropdown(false)}
                selectedMediaTypes={selectedMediaTypes}
                onMediaTypeChange={handleMediaTypeChange}
                allMediaTypes={allMediaTypes}
              />
            )}
          </div>
          {/* Countries/Regions Filter Button and Dropdown Container */}
          <div style={{ position: 'relative', display: 'inline-block' }}> {/* Add positioning context */}
            <div className={`chip ${selectedCountryFilter ? 'active' : ''}`} onClick={handleToggleCountriesDropdown} style={{ cursor: 'pointer' }}>
              Countries/Regions
              {selectedCountryFilter && `: ${selectedCountryFilter}`} {/* Display selected country */}
            </div>
            {/* Countries/Regions Dropdown */}
            {showCountriesDropdown && (
              <CountriesDropdown
                onClose={handleToggleCountriesDropdown}
                selectedCountry={selectedCountryFilter}
                onCountryChange={handleCountryFilterChange}
                countryData={countryData}
              />
            )}
          </div>
            {/* Languages Filter Dropdown (Select) */}
            <div className="languages-filter-select-container">
                <LanguagesFilter
                  languages={availableLanguages}
                  selectedLanguage={selectedLanguageFilter}
                  onLanguageChange={handleLanguagesFilterChange}
                />
            </div>
            {/* Demographics Filter Button and Dropdown Container */}
            <div style={{ position: 'relative', display: 'inline-block' }}> {/* Add positioning context */}
              <button className={`chip ${showDemographicsFilter || activePage === 'demographics' ? 'active' : ''}`} onClick={handleToggleDemographicsFilter}>
                Demographics
                </button>
              {/* Demographics Filter Dropdown */}
              {showDemographicsFilter && (
                <div className="demographics-filter-dropdown">
                  <DemographicsDropdown
                    filters={demographicsFilters}
                    onFilterChange={handleDemographicsFilterChange}
                    onClose={handleToggleDemographicsFilter}
                    allDemographicsData={allDemographicsData} // Pass the fetched data
                  />
                </div>
              )}
              </div>
            <button className="icon-btn">×</button>
            <div style={{ flex: 1 }}></div> {/* Spacer */}
            {/* Date Range Bar */}
                <div className="date-options">
                  {['1D', '7D', '30D', '3M', '6M', '13M'].map(range => (
                    <div
                      key={range}
                      className={`chip${selectedDateRange === range ? ' selected' : ''}`}
                      onClick={() => {
                        setSelectedDateRange(range);
                        setCustomStartDate(null); // Clear custom start date
                        setCustomEndDate(null);   // Clear custom end date
                        fetchAndRender(query);
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      {range}
                    </div>
                  ))}
                  <div
                    key="default"
                    className={`chip${selectedDateRange === '1D' ? ' selected' : ''}`}
                    onClick={() => {
                      setSelectedDateRange('1D');
                      setCustomStartDate(null); // Clear custom start date
                      setCustomEndDate(null);   // Clear custom end date
                      fetchAndRender(query);
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    Default
                  </div>
                </div>
                <div className="date-picker">
                  <FaCalendarAlt />
                  <DatePicker
                    selected={customStartDate}
                    onChange={(date) => setCustomStartDate(date)}
                    selectsStart
                    startDate={customStartDate}
                    endDate={customEndDate}
                    placeholderText="Start Date"
                    dateFormat="yyyy-MM-dd"
                    className="custom-date-input"
                    minDate={availableMinDate}
                    maxDate={availableMaxDate}
                  />
                  <DatePicker
                    selected={customEndDate}
                    onChange={(date) => setCustomEndDate(date)}
                    selectsEnd
                    startDate={customStartDate}
                    endDate={customEndDate}
                    minDate={customStartDate || availableMinDate}
                    maxDate={availableMaxDate}
                    placeholderText="End Date"
                    dateFormat="yyyy-MM-dd"
                    className="custom-date-input"
                  />
                  <button 
                    className="apply-custom-date-btn"
                    onClick={() => {
                      if (customStartDate && customEndDate) {
                        setSelectedDateRange('Custom'); // This will trigger the useEffect to call fetchAndRender
                      }
                    }}
                  >
                    Apply
                  </button>
                </div>
              </div>
          {/* Search Bar */}
          <div className="search-bar-row">
            <div className="search-input-container">
              <span className="hashtag-label">hashtag:</span>
              <input
                type="text"
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="Enter hashtag or topic"
              />
              <FaEllipsisV />
            </div>
            <button className="compare-btn">
              + Compare
            </button>
            <button
              className="search-btn"
              onClick={handleSearch}
              disabled={loading}
            >
              {loading ? 'Loading...' : 'Search'}
            </button>
            </div>
          {/* Main Page Content */}
          {activePage === 'dashboard' ? (
            <>
            {/* Header */}
            <div className="key-metrics-header">
              Key Metrics for <strong>#{query}</strong>
            </div>

            {/* Error Display */}
            {error && (
                <div className="error-message">
                  <span>⚠️</span>
                <div>
                  <strong>Error:</strong> {error}
                    <div>
                    Please check your internet connection and try again. If the problem persists, contact support.
                    </div>
                </div>
              </div>
            )}

            {/* Main Layout Container for Metrics and Charts */}
            <div className="dashboard-layout">
              {/* Metrics Column */}
              <div className="metrics-column">
                {/* Metrics Cards */}
                <div className="metrics">
                  <div className="card">
                    <h3>Result</h3>
                    <p>{resultTotal}</p>
                  </div>
                  <div className="card">
                    <h3>Engagement</h3>
                    <p>{metrics.engagement}</p>
                  </div>
                  <div className="card">
                    <h3>Sentiment</h3>
                    <p>
                      {metrics.sentiment.positive}% <span className="green">Positive</span><br />
                      {metrics.sentiment.negative}% <span className="red">Negative</span><br />
                      {metrics.sentiment.neutral}% <span className="gray">Neutral</span>
                    </p>
                  </div>
                  <div className="card">
                    <h3>Potential Reach</h3>
                    <p>{metrics.potentialReach}</p>
                  </div>
                </div>
              </div>

              {/* Charts Column */}
              <div className="charts-column">
                {/* Charts Container */}
                <div className="charts-container">
                  {!error && (
                      <div className="chart-wrapper">
                        <h3 className="chart-title">Result Over Time</h3>
                        <canvas ref={resultChartRef} />
                      </div>
                  )}

                  {/* Sentiment Chart */}
                  {!error && (
                      <div className="chart-wrapper">
                        <h3 className="chart-title">Net Sentiment Over Time</h3>
                        <canvas ref={sentimentChartRef} />
                      </div>
                  )}
                </div>
              </div>
            </div>

            {/* Articles */}
            <div className="articles-section">
              <h3>
                  Articles {selectedDate ? `for ${new Date(selectedDate).toLocaleDateString()}` : 'All Articles'}
                {selectedDate && (
                  <button 
                    className="clear-selection" 
                    onClick={() => {
                      setSelectedDate(null);
                      setHighlightedArticles([]);
                    }}
                  >
                    Show All
                  </button>
                )}
              </h3>
              {error ? (
                <p>Unable to load articles due to an error.</p>
              ) : allArticles.length === 0 ? (
                <p>No articles found for this search term.</p>
              ) : (
                <ul className="articles-list">
                    {(selectedDate ? highlightedArticles : allArticles).map((article, i) => (
                      <li 
                        key={selectedDate ? `highlighted-${i}` : `article-${i}`}
                        className={`article-item article-box ${selectedDate ? 'highlighted' : ''}`}
                      >
                        <h4>{article.Title}</h4>
                        <p>{article.Description}</p>
                        <div className="article-meta">
                          <span><strong>Source:</strong> {article.Feed}</span>
                          <span><strong>Date:</strong> {article.Date.split('T')[0]}</span>
                          <span><strong>Trust Score:</strong> {(article.Trusted * 100).toFixed(1)}%</span>
                          <a href={article.UrlLink} target="_blank" rel="noopener noreferrer">
                            Read More
                          </a>
                        </div>
                      </li>
                    ))}
                </ul>
              )}
            </div>

            {/* Country Data */}
            <div className="country-section">
              <h3>Country Data {selectedDate && `for ${selectedDate.split('T')[0]}`}</h3>
              {error ? (
                <div className="error-message">
                  <p>Unable to load country data due to an error.</p>
                  <p className="error-details">{error}</p>
                </div>
              ) : countryData.length === 0 ? (
                <div className="no-data-message">
                  <p>No country data available for this search term.</p>
                  <p className="hint">Try searching for a different term or check back later.</p>
                </div>
              ) : (
                <div className="country-data-grid">
                  {/* First render highlighted country data */}
                  {countryData
                    .filter(data => highlightedCountryData.some(highlighted => highlighted.Country === data.Country))
                    .map((data, i) => (
                      <div key={`highlighted-country-${i}`} className="country-data-item highlighted">
                        <h4>{data.CountryName} ({data.Country})</h4>
                        <div className="country-metrics">
                          <div className="metric">
                            <span className="label">Volume:</span>
                            <span className="value">{data.Volume.toLocaleString()}</span>
                          </div>
                          <div className="metric">
                            <span className="label">Sentiment:</span>
                            <span className={`value sentiment ${data.Sentiment >= 0 ? 'positive' : 'negative'}`}>
                              {data.Sentiment > 0 ? '+' : ''}{data.Sentiment.toFixed(2)}
                            </span>
                          </div>
                          <div className="metric">
                            <span className="label">Political Bias:</span>
                            <span className={`value bias ${data.PoliticalBias >= 0 ? 'positive' : 'negative'}`}>
                              {data.PoliticalBias > 0 ? '+' : ''}{data.PoliticalBias.toFixed(4)}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  {/* Then render non-highlighted country data */}
                  {countryData
                    .filter(data => !highlightedCountryData.some(highlighted => highlighted.Country === data.Country))
                    .map((data, i) => (
                      <div key={`unhighlighted-country-${i}`} className="country-data-item">
                        <h4>{data.CountryName} ({data.Country})</h4>
                        <div className="country-metrics">
                          <div className="metric">
                            <span className="label">Volume:</span>
                            <span className="value">{data.Volume.toLocaleString()}</span>
                          </div>
                          <div className="metric">
                            <span className="label">Sentiment:</span>
                            <span className={`value sentiment ${data.Sentiment >= 0 ? 'positive' : 'negative'}`}>
                              {data.Sentiment > 0 ? '+' : ''}{data.Sentiment.toFixed(2)}
                            </span>
                          </div>
                          <div className="metric">
                            <span className="label">Political Bias:</span>
                            <span className={`value bias ${data.PoliticalBias >= 0 ? 'positive' : 'negative'}`}>
                              {data.PoliticalBias > 0 ? '+' : ''}{data.PoliticalBias.toFixed(4)}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              )}
            </div>
          </>
        ) : activePage === 'worldmap' ? (
          <WorldMapPage
            countryData={countryData}
            query={query}
            setQuery={setQuery}
            handleSearch={handleSearch}
            loading={loading}
            startDate={currentStartDate}
            endDate={currentEndDate}
            actualQuery={currentQueryForMap}
          />
        ) : activePage === 'demographics' ? (
            <DemographicsPage query={query} demographicsData={getFilteredDemographicsData()} />
        ) : activePage === 'topthemes' ? (
          <TopThemesPage query={query} setQuery={setQuery} />
          ) : activePage === 'influencer' ? (
            <InfluencerPage query={query} setQuery={setQuery} onSelectInfluencer={handleSelectInfluencer} />
          ) : activePage === 'influencerDetail' && selectedInfluencer ? (
            <InfluencerDetailPage influencer={selectedInfluencer} query={query} onBack={handleBackToInfluencerList} />
        ) : null}
        </div>
      </div>
    </div>
  );
}